import type { Device, Position } from '@/api/types';

/**
 * Vocabulário de status alinhado ao app mobile Fismatek.
 *
 * - `running`  Em Funcionamento → comunicando + ignição ligada (recente)
 * - `ready`    Pronta para Uso  → comunicando + ignição desligada
 * - `blocked`  Bloqueada        → atributo `blocked` true (prioridade sobre os demais)
 * - `off`      Desligada        → sem comunicação há mais de 48h, OU sem dados
 *
 * Regras espelham `LOGICA_STATUS_MAQUINAS_FISMATEK.md` do app mobile,
 * adaptadas para o que o frontend recebe da API Traccar.
 */
export type MachineStatus = 'running' | 'ready' | 'blocked' | 'off';

const OFF_THRESHOLD_MS = 48 * 60 * 60 * 1000;
const RUNNING_FRESH_MS = 30 * 60 * 1000;

/** Threshold acima do qual o GPS é considerado desatualizado. Usado nos KPIs
 *  do Dashboard, separado da lógica de status. */
export const GPS_STALE_MS = 60 * 60 * 1000;

/** Threshold para "Sem comunicação há muito tempo" no Dashboard. */
export const NO_COMM_MS = 10 * 24 * 60 * 60 * 1000;

function attrBool(attrs: Record<string, unknown> | undefined, key: string): boolean | undefined {
  if (!attrs) return undefined;
  const v = attrs[key];
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.toLowerCase() === 'true' || v === '1';
  if (typeof v === 'number') return v !== 0;
  return undefined;
}

function attrNumber(attrs: Record<string, unknown> | undefined, key: string): number | undefined {
  if (!attrs) return undefined;
  const v = attrs[key];
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function attrString(attrs: Record<string, unknown> | undefined, key: string): string | undefined {
  if (!attrs) return undefined;
  const v = attrs[key];
  return typeof v === 'string' && v.trim() ? v : undefined;
}

export function computeMachineStatus(device: Device, position?: Position | null): MachineStatus {
  // Prioridade absoluta: bloqueio explícito vence qualquer outro estado.
  const blocked =
    attrBool(position?.attributes, 'blocked') ?? attrBool(device.attributes, 'blocked');
  if (blocked === true) return 'blocked';

  const lastUpdateMs = position?.fixTime
    ? new Date(position.fixTime).getTime()
    : device.lastUpdate
      ? new Date(device.lastUpdate).getTime()
      : 0;

  // Sem dados ou sem comunicação há mais de 48h: Desligada.
  if (!lastUpdateMs) return 'off';
  const ageMs = Date.now() - lastUpdateMs;
  if (ageMs > OFF_THRESHOLD_MS) return 'off';

  const ignition =
    attrBool(position?.attributes, 'ignition') ?? attrBool(device.attributes, 'ignition');

  // Em Funcionamento: ignição ligada e posição relativamente recente.
  if (ignition === true && ageMs <= RUNNING_FRESH_MS) return 'running';

  // Comunicando dentro de 48h sem ignição ligada (ou ignição stale): Pronta.
  return 'ready';
}

export const STATUS_LABELS: Record<MachineStatus, string> = {
  running: 'Em Funcionamento',
  ready: 'Pronta para Uso',
  blocked: 'Bloqueada',
  off: 'Desligada',
};

/** Ordem de exibição padrão (do mais "ativo" ao menos). */
export const STATUS_ORDER: MachineStatus[] = ['running', 'ready', 'blocked', 'off'];

/** Acessores prontos para os atributos complementares. Centralizar evita
 *  espalhar a leitura de attributes pelos componentes. */
export interface DeviceTelemetry {
  ignition?: boolean;
  motion?: boolean;
  blocked?: boolean;
  charging?: boolean;
  /** voltagem da fonte externa */
  power?: number;
  /** porcentagem 0-100 da bateria interna */
  batteryLevel?: number;
  /** voltagem da bateria interna */
  battery?: number;
  /** texto do alarme se houver */
  alarm?: string;
  odometer?: number;
  hours?: number;
}

export function readTelemetry(position?: Position | null): DeviceTelemetry {
  const a = position?.attributes;
  if (!a) return {};
  return {
    ignition: attrBool(a, 'ignition'),
    motion: attrBool(a, 'motion'),
    blocked: attrBool(a, 'blocked'),
    charging: attrBool(a, 'charge') ?? attrBool(a, 'charging'),
    power: attrNumber(a, 'power'),
    batteryLevel: attrNumber(a, 'batteryLevel'),
    battery: attrNumber(a, 'battery'),
    alarm: attrString(a, 'alarm'),
    odometer: attrNumber(a, 'odometer'),
    hours: attrNumber(a, 'hours'),
  };
}

/** Retorna o "apelido" amigável do device, lendo:
 *  1. attributes.apelido / attributes.nickname
 *  2. device.contact (no app mobile esse campo era usado como apelido)
 *  3. null caso não exista
 */
export function getDeviceNickname(device: Device): string | undefined {
  return (
    attrString(device.attributes, 'apelido') ??
    attrString(device.attributes, 'nickname') ??
    (device.contact?.trim() || undefined)
  );
}

/** Última posição é considerada atualizada se ≤ GPS_STALE_MS. */
export function isGpsFresh(device: Device, position?: Position | null): boolean {
  const ms = position?.fixTime
    ? new Date(position.fixTime).getTime()
    : device.lastUpdate
      ? new Date(device.lastUpdate).getTime()
      : 0;
  if (!ms) return false;
  return Date.now() - ms <= GPS_STALE_MS;
}

/** Sem comunicação há mais de 10 dias (NO_COMM_MS). Usa fixTime se houver,
 *  senão lastUpdate; sem nenhum dos dois conta como sem comunicação. */
export function hasNoCommunication(device: Device, position?: Position | null): boolean {
  const ms = position?.fixTime
    ? new Date(position.fixTime).getTime()
    : device.lastUpdate
      ? new Date(device.lastUpdate).getTime()
      : 0;
  if (!ms) return true;
  return Date.now() - ms > NO_COMM_MS;
}

/** Helper: dispositivo é considerado bloqueado? (mesma fonte usada no status) */
export function isBlocked(device: Device, position?: Position | null): boolean {
  return (
    attrBool(position?.attributes, 'blocked') === true ||
    attrBool(device.attributes, 'blocked') === true
  );
}
